import { useState } from 'react'

// Shared edit / draft / revert / reset state machine for the prompt tab and the
// tools/variables OverrideEditor. Peek + history-open stay with the caller
// (Redux for the prompt tab, local state for OverrideEditor), since that's where
// the two genuinely diverge. The mutation side-effects are injected so each
// caller keeps its own data layer (thunks vs callbacks) and peek-clearing:
//   onSave(draft)     → a truthy result exits edit mode
//   onRevert(version) → a truthy result clears revertTarget + calls onAfterMutate
//   onReset()         → a non-null result closes the reset confirm
//   onAfterMutate()   → caller clears its peek after a successful revert
export const useOverrideEditing = ({ onSave, onRevert, onReset, onAfterMutate }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [revertTarget, setRevertTarget] = useState(null)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)

  const startEdit = (seed) => {
    setDraft(seed || '')
    setEditing(true)
  }

  const cancelEdit = () => setEditing(false)

  const save = async () => {
    const result = await onSave(draft)
    if (result) {
      setEditing(false)
    }
  }

  const confirmRevert = async () => {
    if (!revertTarget) {
      return
    }
    const result = await onRevert(revertTarget.version)
    if (result) {
      setRevertTarget(null)
      onAfterMutate?.()
    }
  }

  const confirmReset = async () => {
    const result = await onReset()
    if (result !== null) {
      setResetConfirmOpen(false)
    }
  }

  return {
    editing,
    draft,
    setDraft,
    revertTarget,
    setRevertTarget,
    resetConfirmOpen,
    setResetConfirmOpen,
    startEdit,
    cancelEdit,
    save,
    confirmRevert,
    confirmReset,
  }
}
